/**
 * Itinerary Submissions API Service
 * Handles all API calls for coordination itinerary submissions
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with auth interceptor
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


// ==================== Interfaces ====================

export type ItinerarySubmissionStatus = 'draft' | 'complete' | 'submitted' | 'reviewed' | 'approved';

export interface StudentProfile {
  id?: number;
  student_number: string;
  gender?: string;
  height?: string;
  weight?: string;
  hair_typecolor?: string;
  facial_hair_typecolor?: string;
  veh_make?: string;
  veh_model?: string;
  veh_color?: string;
  veh_plate?: string;
  created_at?: string;
  updated_at?: string;
}

export interface RouteDataEntry {
  id?: number;
  submission_id?: number;
  entry_order: number;
  route_type: string; // "Pre-route" | "Post-route"
  route_component: string; // "SP" | "CS" | "TP" | etc.
  route_component_other?: string;
  latitude: number;
  longitude: number;
  location_name_address: string;
  arrive_datetime: string; // ISO datetime
  depart_datetime: string; // ISO datetime
  has_documentation: boolean;
  notes?: string;
  created_at?: string;
}

export interface ItineraryAttachment {
  id: number;
  submission_id: number;
  route_entry_id?: number;
  filename: string;
  original_filename: string;
  file_size?: number;
  mime_type?: string;
  uploaded_at: string;
}

export interface ItinerarySubmission {
  id?: number;
  date_submitted?: string;
  student_number: string;
  route_run_datetime: string;
  gender?: string;
  height?: string;
  weight?: string;
  hair_typecolor?: string;
  facial_hair_typecolor?: string;
  veh_make?: string;
  veh_model?: string;
  veh_color?: string;
  veh_plate?: string;
  status?: ItinerarySubmissionStatus;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  route_entries: RouteDataEntry[];
  attachments?: ItineraryAttachment[];
}

export interface ChoiceLists {
  student_numbers: string[];
  route_types: string[];
  route_components: string[];
  genders: string[];
}

export interface ItineraryListResponse {
  items: ItinerarySubmission[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ==================== API Functions ====================

/**
 * Get dropdown choice lists for the form
 */
export const getChoiceLists = async (): Promise<ChoiceLists> => {
  const response = await axiosInstance.get(`/itinerary/choices`);
  return response.data;
};

// ==================== Student Profile APIs ====================

/**
 * Create a new student profile
 */
export const createStudentProfile = async (profile: StudentProfile): Promise<StudentProfile> => {
  const response = await axiosInstance.post(`/itinerary/profiles`, profile);
  return response.data;
};

/**
 * Get a student profile by student number
 */
export const getStudentProfile = async (studentNumber: string): Promise<StudentProfile> => {
  const response = await axiosInstance.get(`/itinerary/profiles/${studentNumber}`);
  return response.data;
};

/**
 * List all student profiles
 */
export const listStudentProfiles = async (): Promise<StudentProfile[]> => {
  const response = await axiosInstance.get(`/itinerary/profiles`);
  return response.data;
};

/**
 * Update a student profile
 */
export const updateStudentProfile = async (
  studentNumber: string,
  updates: Partial<StudentProfile>
): Promise<StudentProfile> => {
  const response = await axiosInstance.put(`/itinerary/profiles/${studentNumber}`, updates);
  return response.data;
};

/**
 * Delete a student profile
 */
export const deleteStudentProfile = async (studentNumber: string): Promise<void> => {
  await axiosInstance.delete(`/itinerary/profiles/${studentNumber}`);
};

export interface StudentProfileBulkDeleteResponse {
  message: string;
  profiles_deleted: number;
  profiles_cleared: number;
  itineraries_archived: number;
  observations_archived: number;
  course_id?: string | null;
  students_affected: number;
}

export const deleteAllStudentProfiles = async (
  courseId: 'DHOST' | 'DHOQC'
): Promise<StudentProfileBulkDeleteResponse> => {
  const response = await axiosInstance.delete(`/itinerary/profiles`, {
    params: {
      confirm: true,
      course_id: courseId,
    },
  });
  return response.data;
};

// ==================== Itinerary Submission APIs ====================

/**
 * Create a new itinerary submission
 */
export const createItinerarySubmission = async (
  submission: ItinerarySubmission
): Promise<ItinerarySubmission> => {
  const response = await axiosInstance.post(`/itinerary/submissions`, submission);
  return response.data;
};

/**
 * Get a specific itinerary submission
 */
export const getItinerarySubmission = async (submissionId: number): Promise<ItinerarySubmission> => {
  const response = await axiosInstance.get(`/itinerary/submissions/${submissionId}`);
  return response.data;
};

/**
 * List itinerary submissions with filters
 */
export const listItinerarySubmissions = async (params?: {
  student_number?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}): Promise<ItineraryListResponse> => {
  const response = await axiosInstance.get(`/itinerary/submissions`, { params });
  return response.data;
};

/**
 * Update an itinerary submission
 */
export const updateItinerarySubmission = async (
  submissionId: number,
  updates: Partial<ItinerarySubmission>
): Promise<ItinerarySubmission> => {
  const response = await axiosInstance.put(`/itinerary/submissions/${submissionId}`, updates);
  return response.data;
};

/**
 * Update a single route entry
 */
export const updateRouteEntry = async (
  entryId: number,
  updates: Partial<RouteDataEntry>
): Promise<RouteDataEntry> => {
  const response = await axiosInstance.put(`/itinerary/route-entries/${entryId}`, updates);
  return response.data;
};

/**
 * Delete an itinerary submission
 */
export const deleteItinerarySubmission = async (submissionId: number): Promise<void> => {
  await axiosInstance.delete(`/itinerary/submissions/${submissionId}`);
};

/**
 * Delete a single route entry from a submission
 */
export const deleteRouteEntry = async (routeEntryId: number): Promise<{ message: string; submission_deleted: boolean; remaining_entries?: number }> => {
  const response = await axiosInstance.delete(`/itinerary/route-entries/${routeEntryId}`);
  return response.data;
};

/**
 * Add a new route entry to an existing submission.
 * This allows adding additional locations (CS, IP, TP, etc.) to a previously submitted itinerary.
 */
export const addRouteEntry = async (
  submissionId: number,
  entry: Omit<RouteDataEntry, 'id' | 'submission_id' | 'created_at'>
): Promise<RouteDataEntry> => {
  const response = await axiosInstance.post(`/itinerary/submissions/${submissionId}/entries`, entry);
  return response.data;
};

// ==================== Student Device Status ====================

export interface StudentDevice {
  device_id: string;
  device_type: string;
  serial_number?: string;
  phone_number?: string;
  last_seen_minutes_ago: number | null;
  last_seen_timestamp: string | null;
  is_online: boolean;
  battery_level?: number;
  has_location: boolean;
}

export interface StudentDevicesResponse {
  student_number: string;
  entity_id: string;
  course_id?: string;
  devices: StudentDevice[];
  total_devices: number;
  online_devices: number;
}

/**
 * Get device status for the current logged-in student
 */
export const getMyDevices = async (): Promise<StudentDevicesResponse> => {
  const response = await axiosInstance.get<StudentDevicesResponse>(`/itinerary/my-devices`);
  return response.data;
};

// ==================== File Attachment APIs ====================

/**
 * Download an attachment file
 */
export const downloadAttachment = async (attachmentId: number, originalFilename: string): Promise<void> => {
  const parseBlobError = async (blob: Blob): Promise<string> => {
    if (!blob.type?.includes('application/json')) {
      return 'Failed to download file';
    }
    const text = await blob.text();
    try {
      const errorData = JSON.parse(text) as { detail?: string };
      return errorData.detail || 'Failed to download file';
    } catch {
      return text || 'Failed to download file';
    }
  };

  try {
    const response = await axiosInstance.get(
      `/itinerary/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );

    const blob = response.data as Blob;
    if (blob.type?.includes('application/json')) {
      throw new Error(await parseBlobError(blob));
    }

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = originalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
      throw new Error(await parseBlobError(err.response.data));
    }
    throw err instanceof Error ? err : new Error('Failed to download file');
  }
};

/**
 * Upload an attachment file to a submission
 */
export const uploadAttachment = async (submissionId: number, file: File, routeEntryId?: number): Promise<{ attachment_id: number; filename: string; file_size: number }> => {
  const token = localStorage.getItem('access_token');
  const formData = new FormData();
  formData.append('file', file);
  
  let url = `${API_BASE_URL}/itinerary/submissions/${submissionId}/upload`;
  if (routeEntryId) {
    url += `?route_entry_id=${routeEntryId}`;
  }
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to upload file: ${response.statusText}`);
  }
  
  return response.json();
};

export default {
  getChoiceLists,
  createStudentProfile,
  getStudentProfile,
  listStudentProfiles,
  updateStudentProfile,
  deleteStudentProfile,
  createItinerarySubmission,
  getItinerarySubmission,
  listItinerarySubmissions,
  updateItinerarySubmission,
  deleteItinerarySubmission,
  deleteRouteEntry,
  addRouteEntry,
  getMyDevices,
  downloadAttachment,
  uploadAttachment,
};
